import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Download, FileSpreadsheet, Upload, X } from 'lucide-react';
import Dropzone from 'react-dropzone';

import Button from 'src/componentes/button';
import Dialog from 'src/componentes/dialog';
import LoadingContainer from 'src/componentes/loading-container';
import http from 'src/services/httpService';
import { DownloadFile, ParseBlobError } from 'src/services/downloadFileUrl';
import errorHandler from 'src/utils/error-handler';
import css from 'src/utils/css';
import {
  FORMULARIO_GLOBAL_QUESTION_TYPES,
  getQuestionMatrixValidationErrors,
  normalizeFormularioGlobalQuestionType,
  normalizeQuestionMatrixSettings,
} from './FormularioGlobalQuestionTypes';

const normalizeImportedQuestion = (question) => {
  const tipo = normalizeFormularioGlobalQuestionType(
    question.tipo || question.tipoPergunta
  );
  if (tipo !== FORMULARIO_GLOBAL_QUESTION_TYPES.MATRIZ_SELECAO) return question;

  return normalizeQuestionMatrixSettings({
    ...question,
    tipo,
    opcoes: [],
    linhas: [...question.linhas].sort(
      (a, b) => (a.ordem || 0) - (b.ordem || 0)
    ),
    colunas: [...question.colunas].sort(
      (a, b) => (a.ordem || 0) - (b.ordem || 0)
    ),
  });
};

const getImportedQuestionError = (question) => {
  if (!question || typeof question !== 'object' || Array.isArray(question)) {
    return 'msgRetornoImportacaoFormularioInvalido';
  }

  const tipo = normalizeFormularioGlobalQuestionType(
    question.tipo || question.tipoPergunta
  );
  if (tipo !== FORMULARIO_GLOBAL_QUESTION_TYPES.MATRIZ_SELECAO) return null;

  for (const field of ['linhas', 'colunas']) {
    if (
      !Array.isArray(question[field]) ||
      question[field].some(
        (item) => !item || typeof item !== 'object' || Array.isArray(item)
      )
    ) {
      return field === 'linhas'
        ? 'msgMatrizLinhasInvalidas'
        : 'msgMatrizColunasInvalidas';
    }
  }

  if (
    question.permitirMultiplaSelecaoPorLinha != null &&
    typeof question.permitirMultiplaSelecaoPorLinha !== 'boolean'
  ) {
    return 'msgRetornoImportacaoFormularioInvalido';
  }

  return (
    Object.values(
      getQuestionMatrixValidationErrors({ ...question, tipo })
    )[0] || null
  );
};

const classes = css`
  .footer {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    justify-content: space-between;
    margin-bottom: 15px;
    margin-top: 20px;
  }
  .exports,
  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
  .actions {
    justify-content: flex-end;
    margin-left: auto;
  }
  .file {
    align-items: center;
    background: #f8fafc;
    border: 1px dashed #cbd5e1;
    border-radius: 8px;
    color: #475569;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 12px;
    justify-content: center;
    margin-top: 20px;
    min-height: 150px;
    padding: 24px;
    text-align: center;
    width: 100%;
  }
  .file:hover,
  .file:focus-visible,
  .fileActive {
    background: #ecfeff;
    border-color: #0f7180;
  }
  .file[aria-disabled='true'] {
    cursor: not-allowed;
    opacity: 0.55;
  }
  .fileRejected {
    border-color: #b91c1c;
  }
  .selectedFile {
    align-items: center;
    display: flex;
    gap: 12px;
    margin-top: 12px;
  }
  .fileName {
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .error {
    color: #b91c1c;
    margin-top: 12px;
  }
`;

const FormularioGlobalImportacao = ({
  disabled,
  formulario,
  onImportar,
  onClose,
}) => {
  const intl = useIntl();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const busyRef = useRef(false);
  const dropzoneRef = useRef(null);
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  const handleSelectFile = (acceptedFiles, rejectedFiles) => {
    if (disabledRef.current || busyRef.current) return;
    if (rejectedFiles.length > 0) {
      setFile(null);
      setError(intl.formatMessage({ id: 'msgArquivoFormularioNaoAceito' }));
      return;
    }
    if (acceptedFiles.length === 1) {
      setFile(acceptedFiles[0]);
      setError('');
    }
  };

  const run = async (action) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError('');
    try {
      await action();
    } catch (err) {
      errorHandler(await ParseBlobError(err));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const handleExport = (somenteCabecalho) =>
    run(async () => {
      const response = await http.post(
        somenteCabecalho
          ? '/FormularioGlobal/ExportarCabecalho'
          : '/FormularioGlobal/Exportar',
        somenteCabecalho
          ? { tipoFormulario: formulario?.tipoFormulario }
          : formulario,
        { responseType: 'blob' }
      );
      DownloadFile(response);
    });

  const handleImport = () =>
    run(async () => {
      if (disabledRef.current || !file) return;
      const formData = new FormData();
      formData.append('arquivo', file);
      formData.append(
        'tipoFormulario',
        formulario?.tipoFormulario?.codigo || ''
      );
      const response = await http.post('/FormularioGlobal/Importar', formData);
      if (disabledRef.current) return;
      const imported = response.data?.model || response.data;
      if (
        !imported ||
        !Array.isArray(imported.secoes) ||
        !Array.isArray(imported.perguntas) ||
        imported.secoes.some(
          (section) => !section || !Array.isArray(section.perguntas)
        )
      ) {
        setError(
          intl.formatMessage({ id: 'msgRetornoImportacaoFormularioInvalido' })
        );
        return;
      }
      const questions = [
        ...imported.perguntas,
        ...imported.secoes.flatMap((section) => section.perguntas),
      ];
      for (const question of questions) {
        const errorId = getImportedQuestionError(question);
        if (errorId) {
          setError(
            `${
              question?.titulo ? `${question.titulo}: ` : ''
            }${intl.formatMessage({ id: errorId })}`
          );
          return;
        }
      }

      onImportar({
        ...imported,
        perguntas: imported.perguntas.map(normalizeImportedQuestion),
        secoes: imported.secoes.map((section) => ({
          ...section,
          perguntas: section.perguntas.map(normalizeImportedQuestion),
        })),
      });
      onClose();
    });

  return (
    <Dialog
      title="labelImportarFormulario"
      width={760}
      padContent
      textCapitalizeTitle={false}
      titleCapitalizeNone
      onRequestClose={() => !busyRef.current && onClose()}
    >
      <LoadingContainer isLoading={busy}>
        <div>
          <p>{intl.formatMessage({ id: 'msgInstrucoesFormularioPlanilha' })}</p>
          <Dropzone
            ref={dropzoneRef}
            className={classes.file}
            activeClassName={classes.fileActive}
            rejectClassName={classes.fileRejected}
            accept=".xlsx,.xls,.csv"
            multiple={false}
            disablePreview
            disableClick={disabled || busy}
            inputProps={{
              disabled: disabled || busy,
              'aria-label': intl.formatMessage({
                id: 'labelImportarFormulario',
              }),
            }}
            role="button"
            tabIndex={disabled || busy ? -1 : 0}
            aria-disabled={disabled || busy}
            onDrop={handleSelectFile}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
                if (!disabledRef.current && !busyRef.current) {
                  dropzoneRef.current?.open();
                }
              }
            }}
          >
            <Upload size={28} aria-hidden="true" />
            <span>{intl.formatMessage({ id: 'label.cliqueOuArraste' })}</span>
          </Dropzone>
          {file && (
            <div className={classes.selectedFile}>
              <FileSpreadsheet size={20} aria-hidden="true" />
              <span className={classes.fileName}>{file.name}</span>
              <Button
                type="button"
                color="link"
                leftIcon={X}
                disabled={disabled || busy}
                onClick={() => {
                  setFile(null);
                  setError('');
                }}
              >
                {intl.formatMessage({ id: 'labelRemoverArquivo' })}
              </Button>
            </div>
          )}
          {error && (
            <div className={classes.error} role="alert">
              {error}
            </div>
          )}
          <div className={classes.footer}>
            <div className={classes.exports}>
              <Button
                type="button"
                color="secondary"
                leftIcon={Download}
                disabled={busy}
                onClick={() => handleExport(false)}
              >
                {intl.formatMessage({ id: 'labelExportarFormularioAtual' })}
              </Button>
              <Button
                type="button"
                color="secondary"
                leftIcon={Download}
                disabled={busy}
                onClick={() => handleExport(true)}
              >
                {intl.formatMessage({ id: 'label.exportarCabecalho' })}
              </Button>
            </div>
            <div className={classes.actions}>
              <Button
                type="button"
                color="secondary"
                disabled={busy}
                onClick={onClose}
              >
                {intl.formatMessage({ id: 'label.cancelar' })}
              </Button>
              <Button
                type="button"
                leftIcon={Upload}
                disabled={disabled || busy || !file}
                onClick={handleImport}
              >
                {intl.formatMessage({ id: 'importar' })}
              </Button>
            </div>
          </div>
        </div>
      </LoadingContainer>
    </Dialog>
  );
};

export default FormularioGlobalImportacao;
